import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import get from 'lodash/get';
import Grid from '@material-ui/core/Grid';
import Card from '@material-ui/core/Card';
import CardHeader from '@material-ui/core/CardHeader';
import CardMedia from '@material-ui/core/CardMedia';
import { getWidgetLibrary, getActiveUnitLOInsights } from '../../../../../core/api';
import Loader from '../../../Loader';
import MLButton from '../../../../../../components/MLButton';
import topLosToReview from './images/top-los-to-review.png';
import topAssignmentsToReview from './images/top-assignments-to-review.png';
import topAssignmentScoreChart from './images/top-assignment-score.png';
import loginPieChart from './images/login-pie-chart.png';
import timeScorePreview from './images/time-score-preview.png';
import timeAttemptsPreview from './images/time-attempts-preview.png';
import completedAssignment from './images/completed-assignment.png';
import style from './index.module.css';
import {
  WIDGET_TYPE_TOP_LO_BAR_CHART,
  WIDGET_TYPE_TOP_ASSIGNMENTS_BAR_CHART,
  WIDGET_TYPE_TOP_EXTERNAL_LO_BAR_CHART,
  WIDGET_TYPE_ASSIGNMENT_SCORE_HISTOGRAM_CHART,
  WIDGET_TYPE_LOGIN_PIE_CHART,
  WIDGET_TYPE_TIME_SCORE_CHART,
  WIDGET_TYPE_TIME_ATTEMPTS_CHART,
  TIME_SCORE_HELPER_TEXT,
  TIME_ATTEMPTS_HELPER_TEXT,
  WIDGET_TYPE_COMPLETED_ASSIGNMENT,
} from '../../../../constants';
import BetaBadge from '../BetaBadge/BetaBadge';
import { getConfigObject } from '../../../../../util/config';

const getWidgetImageSrc = (widgetType) => {
  let imageSRC;
  switch (widgetType) {
    case WIDGET_TYPE_TOP_LO_BAR_CHART:
      imageSRC = topLosToReview;
      break;
    case WIDGET_TYPE_TOP_ASSIGNMENTS_BAR_CHART:
      imageSRC = topAssignmentsToReview;
      break;
    case WIDGET_TYPE_TOP_EXTERNAL_LO_BAR_CHART:
      imageSRC = topLosToReview;
      break;
    case WIDGET_TYPE_ASSIGNMENT_SCORE_HISTOGRAM_CHART:
      imageSRC = topAssignmentScoreChart;
      break;
    case WIDGET_TYPE_LOGIN_PIE_CHART:
      imageSRC = loginPieChart;
      break;
    case WIDGET_TYPE_TIME_SCORE_CHART:
      imageSRC = timeScorePreview;
      break;
    case WIDGET_TYPE_TIME_ATTEMPTS_CHART:
      imageSRC = timeAttemptsPreview;
      break;
    case WIDGET_TYPE_COMPLETED_ASSIGNMENT:
      imageSRC = completedAssignment;
      break;
    default:
      break;
  }
  return imageSRC;
};

function WidgetCard({ widget, handleAddWidget, isBeta, helperText }) {
  const [isAdding, setIsAdding] = useState(false);
  const handleClick = async () => {
    setIsAdding(true);
    await handleAddWidget(widget.widget_id);
    setIsAdding(false);
  };
  const betaSubheaderClass = isBeta ? style.subheader : null;
  return (
    <div className={`${isBeta || helperText ? style.betaCardWrapper : ''}`}>
      <Card className={style.card} variant="outlined">
        <CardHeader
          className={isBeta ? style.headerBeta : style.header}
          classes={{ content: style.headerContent, subheader: betaSubheaderClass }}
          action={
            <MLButton
              data-id="__add_widget"
              disabled={isAdding}
              btnType="primary"
              type="submit"
              btnText="Add Insight"
              testId="add_widget_btn"
              onClick={handleClick}
              className={isBeta ? style.buttonBeta : ''}
            />
          }
          title={widget.title_summary}
          subheader={isBeta ? helperText : null}
          titleTypographyProps={{ className: style.title }}
        />
        <CardMedia
          className={style.media}
          image={getWidgetImageSrc(widget.widget_type)}
          title={widget.title_summary}
        />
      </Card>
      {isBeta && <BetaBadge className={style.beta} />}
    </div>
  );
}
WidgetCard.propTypes = {
  widget: PropTypes.object,
  handleAddWidget: PropTypes.func,
  isBeta: PropTypes.bool,
  helperText: PropTypes.string,
};

const isBeta = widget => widget.widget_type === WIDGET_TYPE_TIME_SCORE_CHART || widget.widget_type === WIDGET_TYPE_TIME_ATTEMPTS_CHART;
const getHelperText = (widget) => {
  let helperText = null;
  switch (widget.widget_type) {
    case WIDGET_TYPE_TIME_SCORE_CHART:
      helperText = TIME_SCORE_HELPER_TEXT;
      break;
    case WIDGET_TYPE_TIME_ATTEMPTS_CHART:
      helperText = TIME_ATTEMPTS_HELPER_TEXT;
      break;
    default:
      break;
  }
  return helperText;
};

export default function WidgetLibrary({ courseId, dashboardId, handleAddWidget, handleWidgetError }) {
  const [data, setData] = useState({ widgets: [] });
  const [isLoading, setIsLoading] = useState(true);

  const {
    FEATURE_USE_EXTERNAL_INSIGHTS,
    FEATURE_USE_SCORE_HISTOGRAM_CHART,
    FEATURE_USE_LOGIN_INSIGHTS,
    FEATURE_USE_TIME_SCORE_CHART,
    FEATURE_USE_TIME_ATTEMPTS_CHART,
    FEATURE_USE_COMPLETED_ASSIGNMENT,
  } = getConfigObject();

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      let widgetsData = [];
      try {
        const response = await getWidgetLibrary(courseId);
        if (!response.error) {
          widgetsData = response;
        } else {
          throw new Error();
        }

        if (FEATURE_USE_EXTERNAL_INSIGHTS) {
          const LOData = await getActiveUnitLOInsights(courseId, 0, {});
          if (!LOData.error) {
            const externalInsightsData = get(LOData, 'activeUnit.externalFrameworkBranches', []);
            if (externalInsightsData.length) {
              const frameworkName = get(externalInsightsData, '[0].externalFrameworkName', 'external framework');
              const index = widgetsData.findIndex(widget => widget.widget_type === WIDGET_TYPE_TOP_EXTERNAL_LO_BAR_CHART);
              widgetsData = [
                ...widgetsData.slice(0, index),
                { ...widgetsData[index], title_summary: frameworkName },
                ...widgetsData.slice(index + 1),
              ];
            } else {
              widgetsData = widgetsData.filter(widget => widget.widget_type !== WIDGET_TYPE_TOP_EXTERNAL_LO_BAR_CHART);
            }
          }
        }
        setData({ widgets: widgetsData });
      } catch (error) {
        handleWidgetError('Error fetching widgets.');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [dashboardId]);

  if (isLoading) return <Loader />;


  return (
    <div className={style.root}>
      <Grid container spacing={3}>
        {data.widgets
          .filter(widget =>
            (
              (FEATURE_USE_EXTERNAL_INSIGHTS || widget.widget_type !== WIDGET_TYPE_TOP_EXTERNAL_LO_BAR_CHART) &&
              (FEATURE_USE_SCORE_HISTOGRAM_CHART || widget.widget_type !== WIDGET_TYPE_ASSIGNMENT_SCORE_HISTOGRAM_CHART) &&
              (FEATURE_USE_TIME_SCORE_CHART || widget.widget_type === WIDGET_TYPE_TIME_SCORE_CHART) &&
              (FEATURE_USE_TIME_ATTEMPTS_CHART || widget.widget_type === WIDGET_TYPE_TIME_ATTEMPTS_CHART) &&
              (FEATURE_USE_LOGIN_INSIGHTS || widget.widget_type !== WIDGET_TYPE_LOGIN_PIE_CHART) &&
              (FEATURE_USE_COMPLETED_ASSIGNMENT || widget.widget_type !== WIDGET_TYPE_COMPLETED_ASSIGNMENT)
            )
          )
          .map(widget => <Grid key={widget.widget_id} item xs={12} sm={12} md={6} lg={4}>
            <WidgetCard widget={widget} handleAddWidget={handleAddWidget} isBeta={isBeta(widget)} helperText={getHelperText(widget)} />
          </Grid>)
        }
      </Grid>
    </div>
  );
}
WidgetLibrary.propTypes = {
  courseId: PropTypes.string,
  dashboardId: PropTypes.string,
  handleAddWidget: PropTypes.func,
  handleWidgetError: PropTypes.func,
};
